import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [wishlist, setWishlist] = useState([]);

  const refreshCart = useCallback(async () => {
    if (!user) { setCart({ items: [], subtotal: 0 }); return; }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch { /* ignore */ }
  }, [user]);

  const refreshWishlist = useCallback(async () => {
    if (!user) { setWishlist([]); return; }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refreshCart(); refreshWishlist(); }, [refreshCart, refreshWishlist]);

  const add = async (book_id) => {
    if (!user) { toast.error("Connectez-vous pour ajouter au panier"); return false; }
    await api.post("/cart/add", { book_id, quantity: 1 });
    await refreshCart();
    toast.success("Ajouté au panier");
    return true;
  };
  const remove = async (book_id) => {
    await api.post("/cart/remove", { book_id, quantity: 1 });
    await refreshCart();
  };
  const clear = async () => { await api.post("/cart/clear"); await refreshCart(); };

  const toggleWishlist = async (book_id) => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris"); return; }
    const { data } = await api.post("/wishlist/toggle", { book_id });
    await refreshWishlist();
    toast.success(data.in_wishlist ? "Ajouté aux favoris" : "Retiré des favoris");
  };
  const isInWishlist = (id) => wishlist.some((b) => b.id === id);

  const count = cart.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ cart, wishlist, count, add, remove, clear, refreshCart, toggleWishlist, isInWishlist }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
